import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { EditorForm } from "./shared/EditorForm";
import { EditorField } from "./shared/EditorField";
import { EditorHeader } from "./EditorHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Pencil, Trash2 } from "lucide-react";

interface CapTableEntry {
  id: string;
  name: string;
  percentage: number;
  shares: number;
  color: string;
}

interface CapTableEditorProps {
  onSuccess?: () => void;
}

export const CapTableEditor = ({ onSuccess }: CapTableEditorProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [entries, setEntries] = useState<CapTableEntry[]>([]);
  const [editingEntry, setEditingEntry] = useState<CapTableEntry | null>(null);
  const { toast } = useToast();

  const fetchEntries = async () => {
    const { data, error } = await supabase
      .from('cap_table_entries')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching entries:', error);
      return;
    }

    setEntries(data || []);
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      const entry = {
        name: String(formData.get('name')),
        percentage: parseFloat(String(formData.get('percentage'))),
        shares: parseInt(String(formData.get('shares'))),
        color: String(formData.get('color'))
      };

      let error;
      
      if (editingEntry) {
        // Update existing entry
        ({ error } = await supabase
          .from('cap_table_entries')
          .update(entry)
          .eq('id', editingEntry.id));
      } else {
        // Insert new entry
        ({ error } = await supabase
          .from('cap_table_entries')
          .insert([entry]));
      }

      if (error) throw error;

      toast({
        title: "Success",
        description: editingEntry 
          ? "Cap table entry updated successfully"
          : "Cap table entry added successfully",
      });

      // Reset form and editing state
      e.currentTarget.reset();
      setEditingEntry(null);
      
      // Refresh data
      fetchEntries();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to save cap table entry",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (entry: CapTableEntry) => {
    setEditingEntry(entry);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('cap_table_entries')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Cap table entry deleted successfully",
      });

      fetchEntries();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to delete cap table entry",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    setEditingEntry(null);
  };

  return (
    <div className="space-y-6">
      <EditorHeader 
        title="Cap Table Editor"
        description={editingEntry ? "Edit stakeholder details" : "Add new stakeholders to the cap table"}
      />
      <EditorForm onSubmit={handleSubmit} isSubmitting={isSubmitting}>
        <div className="space-y-4">
          <EditorField
            label="Name"
            name="name"
            type="text"
            required
            defaultValue={editingEntry?.name}
          />
          <EditorField
            label="Percentage"
            name="percentage"
            type="number"
            required
            defaultValue={editingEntry?.percentage}
          />
          <EditorField
            label="Shares"
            name="shares"
            type="number"
            required
            defaultValue={editingEntry?.shares}
          />
          <EditorField
            label="Color"
            name="color"
            type="color"
            required
            defaultValue={editingEntry?.color}
          />
        </div>
        <div className="flex gap-2 mt-4">
          <Button type="submit" disabled={isSubmitting}>
            {editingEntry ? 'Update Entry' : 'Add Entry'}
          </Button>
          {editingEntry && (
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          )}
        </div>
      </EditorForm>

      {/* Existing Entries */}
      <div className="grid gap-4">
        {entries.map((entry) => (
          <Card key={entry.id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div 
                  className="w-4 h-4 rounded-full" 
                  style={{ backgroundColor: entry.color }}
                />
                <div>
                  <h3 className="font-medium">{entry.name}</h3>
                  <p className="text-sm text-gray-500">
                    {entry.percentage}% ({entry.shares.toLocaleString()} shares)
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleEdit(entry)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(entry.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
